import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BarChart3, Clock, Users, Calendar, TrendingUp, 
  Download, Loader2, RefreshCw, ChevronDown, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HRReports = ({ isAdmin = true, currentUserId = null }) => {
  const [loading, setLoading] = useState(true);
  const [visitStats, setVisitStats] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [monthlyOverview, setMonthlyOverview] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [staffFilter, setStaffFilter] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [activeView, setActiveView] = useState('stats'); // 'stats', 'daily', 'monthly'

  useEffect(() => {
    loadData();
    if (isAdmin) loadStaffList();
  }, [selectedDate, selectedMonth, selectedYear, staffFilter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadStaffList = async () => {
    try {
      const response = await axios.get(`${API}/hr/reports/staff-list`, getAuthHeaders());
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Error loading staff list:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Date range for stats (last 30 days by default)
      const dateTo = new Date().toISOString().split('T')[0];
      const dateFrom = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const requests = [
        axios.get(
          `${API}/hr/reports/visit-stats?date_from=${dateFrom}&date_to=${dateTo}${staffFilter ? `&staff_id=${staffFilter}` : ''}`,
          getAuthHeaders()
        ).catch(() => ({ data: { statistics: [] } })),
        
        axios.get(
          `${API}/hr/reports/monthly-overview?month=${selectedMonth}&year=${selectedYear}`,
          getAuthHeaders()
        ).catch(() => ({ data: null }))
      ];
      
      if (isAdmin) {
        requests.push(
          axios.get(`${API}/hr/reports/daily-summary?date=${selectedDate}`, getAuthHeaders())
            .catch(() => ({ data: null }))
        );
      }
      
      const [statsRes, monthlyRes, dailyRes] = await Promise.all(requests);
      
      setVisitStats(statsRes.data.statistics || []);
      setMonthlyOverview(monthlyRes.data);
      if (dailyRes) setDailySummary(dailyRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei report');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('it-IT');
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '-';
    return `${num.toFixed(1)}%`;
  };

  const getRoleLabel = (role) => {
    if (role === 'doctor') return 'Oculista';
    if (role === 'ortottista') return 'Ortottista';
    return role;
  };

  const getRoleColor = (role) => {
    if (role === 'doctor') return 'bg-blue-100 text-blue-800';
    if (role === 'ortottista') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="hr-reports">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Reportistica HR</h2>
          <p className="text-gray-500">
            {isAdmin ? 'Analisi performance e statistiche staff' : 'Le tue statistiche'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-1 inline-flex">
        <button
          onClick={() => setActiveView('stats')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeView === 'stats' ? 'bg-sky-100 text-sky-800' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline mr-2" />
          Statistiche Staff
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveView('daily')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeView === 'daily' ? 'bg-sky-100 text-sky-800' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Riepilogo Giornaliero
          </button>
        )}
        <button
          onClick={() => setActiveView('monthly')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeView === 'monthly' ? 'bg-sky-100 text-sky-800' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Panoramica Mensile
        </button>
      </div>

      {/* Filtri */}
      {activeView === 'stats' && isAdmin && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
              <select
                value={staffFilter}
                onChange={(e) => setStaffFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                data-testid="staff-filter"
              >
                <option value="">Tutti</option>
                {staffList.map(s => (
                  <option key={s.id} value={s.id}>
                    {getRoleLabel(s.role)} - {s.first_name} {s.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {activeView === 'daily' && isAdmin && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                data-testid="date-filter"
              />
            </div>
          </div>
        </div>
      )}

      {activeView === 'monthly' && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mese</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
              >
                {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>
                    {new Date(2024, m - 1, 1).toLocaleString('it-IT', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anno</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
              >
                {[2024, 2025, 2026].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      ) : (
        <>
          {/* Stats View */}
          {activeView === 'stats' && (
            <div className="space-y-4">
              {visitStats.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Nessuna statistica disponibile per il periodo selezionato</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visitStats.map((stat, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-sm p-6" data-testid={`staff-stat-${stat.staff_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-800">{stat.staff_name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getRoleColor(stat.staff_role)}`}>
                            {getRoleLabel(stat.staff_role)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Visite Totali</p>
                          <p className="text-2xl font-bold text-gray-800">{formatNumber(stat.total_visits)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Completate</p>
                          <p className="text-2xl font-bold text-green-600">{formatNumber(stat.completed_visits)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Cancellate</p>
                          <p className="text-xl font-bold text-red-500">{formatNumber(stat.cancelled_visits)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">% Completamento</p>
                          <p className="text-xl font-bold text-sky-600">{formatPercent(stat.completion_rate)}</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Attesa media
                          </span>
                          <span className="font-medium">
                            {stat.avg_wait_time_minutes > 0 ? `${stat.avg_wait_time_minutes} min` : '-'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-2">
                          <span className="text-gray-500">
                            <Users className="w-4 h-4 inline mr-1" />
                            Pazienti visti
                          </span>
                          <span className="font-medium">{formatNumber(stat.patients_seen)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Daily Summary View */}
          {activeView === 'daily' && isAdmin && dailySummary && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Programmati</p>
                  <p className="text-3xl font-bold text-gray-800">{dailySummary.summary?.total_scheduled || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Completati</p>
                  <p className="text-3xl font-bold text-green-600">{dailySummary.summary?.completed || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Cancellati</p>
                  <p className="text-3xl font-bold text-red-500">{dailySummary.summary?.cancelled || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">No Show</p>
                  <p className="text-3xl font-bold text-orange-500">{dailySummary.summary?.no_show || 0}</p>
                </div>
              </div>

              {/* Wait Times */}
              {dailySummary.wait_times && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-sky-600" />
                    Tempi di Attesa
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Attesa Media</p>
                      <p className="text-2xl font-bold text-gray-800">
                        {dailySummary.wait_times.average_minutes || 0} min
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Attesa Massima</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {dailySummary.wait_times.max_minutes || 0} min
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* By Room */}
              {dailySummary.by_room && Object.keys(dailySummary.by_room).length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Per Stanza</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(dailySummary.by_room).map(([room, data]) => (
                      <div key={room} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-700">{room}</h4>
                        <div className="flex justify-between mt-2">
                          <span className="text-sm text-gray-500">Programmati: {data.scheduled}</span>
                          <span className="text-sm text-green-600">Completati: {data.completed}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Staff */}
              {dailySummary.by_staff && dailySummary.by_staff.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Per Staff</h3>
                  <div className="space-y-3">
                    {dailySummary.by_staff.map((staff, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getRoleColor(staff.role)}`}>
                            {getRoleLabel(staff.role)}
                          </span>
                          <span className="font-medium">{staff.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-500">Prog: {staff.scheduled}</span>
                          <span className="text-green-600 font-medium">Compl: {staff.completed}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Monthly Overview */}
          {activeView === 'monthly' && monthlyOverview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Totale Appuntamenti</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {formatNumber(monthlyOverview.summary?.total_appointments)}
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Completati</p>
                  <p className="text-3xl font-bold text-green-600">
                    {formatNumber(monthlyOverview.summary?.completed)}
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Cancellati</p>
                  <p className="text-3xl font-bold text-red-500">
                    {formatNumber(monthlyOverview.summary?.cancelled)}
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <p className="text-sm text-gray-500">Fatturato Stimato</p>
                  <p className="text-3xl font-bold text-sky-600">
                    €{formatNumber(monthlyOverview.summary?.estimated_revenue)}
                  </p>
                </div>
              </div>

              {/* Completion Rate */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Tasso di Completamento</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div 
                      className="bg-sky-500 h-full transition-all duration-500"
                      style={{ width: `${monthlyOverview.summary?.completion_rate || 0}%` }}
                    />
                  </div>
                  <span className="text-xl font-bold text-sky-600">
                    {formatPercent(monthlyOverview.summary?.completion_rate)}
                  </span>
                </div>
              </div>

              {/* Daily Breakdown */}
              {monthlyOverview.daily_breakdown && Object.keys(monthlyOverview.daily_breakdown).length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Dettaglio Giornaliero</h3>
                  <div className="overflow-x-auto">
                    <div className="flex gap-1" style={{ minWidth: '100%' }}>
                      {Object.entries(monthlyOverview.daily_breakdown)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([date, data]) => {
                          const day = new Date(date).getDate();
                          const height = Math.max(20, (data.completed / Math.max(...Object.values(monthlyOverview.daily_breakdown).map(d => d.total || 1))) * 60);
                          
                          return (
                            <div key={date} className="flex flex-col items-center flex-1 min-w-[20px]" title={`${date}: ${data.completed}/${data.total}`}>
                              <div 
                                className="w-full bg-sky-500 rounded-t transition-all hover:bg-sky-600"
                                style={{ height: `${height}px` }}
                              />
                              <span className="text-[10px] text-gray-400 mt-1">{day}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HRReports;
